/**
 * Salesforce-only authentication context.
 * Handles Salesforce OAuth: login, logout, refresh, applyTokenSet (to SF storage only).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  augmentAuthorizeUrlWithPkce,
  buildAuthorizeUrl,
  fetchUserInfo,
  readEnv,
  refreshToken as refreshTokenApi,
  revokeToken,
} from "./oauth";
import {
  refreshTokenViaBackend,
  clearSfCookiesViaBackend,
} from "../services/backend/sfAuth";
import { SfAuth, storage, type TokenSet } from "./storage";
import { getEnv } from "../config/getEnv";
import { isSfSessionExpired } from "./sfSessionLimits";

type User = Record<string, unknown> | null;

/** Determine if Salesforce auth is present and valid from storage only. */
function isSfAuthValidFromStorage(): boolean {
  const sf = storage.readSfAuthSession();
  return Boolean(sf?.tokenSet?.access_token);
}

/** Read current SF auth from storage. */
function getSfAuthFromStorage(): SfAuth | undefined {
  return storage.readSfAuthSession();
}

export type SalesforceAuthContextValue = {
  isAuthenticated: boolean;
  accessToken?: string;
  instanceUrl?: string;
  idToken?: string;
  user: User;
  isUserLoading: boolean;
  login: (interactive?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  applyTokenSet: (tokenSet: TokenSet) => void;
  logoutAppOnly: () => void;
};

export const SalesforceAuthContext = createContext<
  SalesforceAuthContextValue | undefined
>(undefined);

function getTokenExpiryMs(tokenSet?: TokenSet): number | undefined {
  if (!tokenSet?.issued_at || !tokenSet?.expires_in) return undefined;
  return tokenSet.issued_at + tokenSet.expires_in * 1000;
}

export function SalesforceAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const env = useMemo(() => readEnv(), []);
  const [storageRevision, setStorageRevision] = useState<number>(0);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const refreshInFlight = useRef<Promise<void> | null>(null);

  const stored = useMemo(
    () => getSfAuthFromStorage(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storageRevision triggers re-read
    [storageRevision],
  );
  const isAuthenticated = useMemo(
    () => isSfAuthValidFromStorage(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storageRevision triggers re-read
    [storageRevision],
  );

  const setAuthAndPersist = useCallback((next: SfAuth | undefined) => {
    if (next) storage.saveSfAuthSession(next, true);
    else storage.clearSfAuthSession();
    setStorageRevision((r) => r + 1);
  }, []);

  const doLogin = useCallback(
    async (interactive: boolean = false) => {
      try {
        storage.clearTemp();
        storage.clearSfAuthSession();
      } catch {}
      const opts = interactive
        ? { login_hint: env.LOGIN_HINT }
        : { prompt: "none" as const, login_hint: env.LOGIN_HINT };
      const url = await augmentAuthorizeUrlWithPkce(
        buildAuthorizeUrl(env, opts),
      );
      window.location.assign(url.toString());
    },
    [env],
  );

  const doLogout = useCallback(async () => {
    const tokenSet = storage.readSfAuthSession()?.tokenSet;
    try {
      if (tokenSet?.access_token) await revokeToken(env, tokenSet.access_token);
      if (tokenSet?.refresh_token)
        await revokeToken(env, tokenSet.refresh_token);
    } catch {
      // ignore revoke failures
    }
    try {
      if (getEnv("REACT_APP_BE_URL")) await clearSfCookiesViaBackend();
    } catch {
      // ignore backend cookie clear failures
    } finally {
      storage.clearSfAuthSession();
      setStorageRevision((r) => r + 1);
    }
  }, [env]);

  const logoutAppOnly = useCallback(() => {
    try {
      window.sessionStorage.setItem("SF_DISABLE_SILENT_ONCE", "1");
    } catch {}
    if (getEnv("REACT_APP_BE_URL")) {
      clearSfCookiesViaBackend().catch(() => {});
    }
    storage.clearSfAuthSession();
    setStorageRevision((r) => r + 1);
  }, []);

  const doRefresh = useCallback(async () => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const current = storage.readSfAuthSession();
    const refresh = current?.tokenSet?.refresh_token;
    const useBackend = Boolean(getEnv("REACT_APP_BE_URL"));
    if (!useBackend && !refresh) return;
    if (isSfSessionExpired()) {
      await doLogout();
      try {
        if (!window.location.pathname.startsWith("/login")) {
          window.location.assign("/login?error=session_expired");
        }
      } catch {}
      return;
    }
    const run = async () => {
      try {
        let nextToken: TokenSet;
        if (useBackend) {
          // Single refresh request: cookies and/or body refresh_token (no chained retries).
          nextToken = await refreshTokenViaBackend({
            ...(refresh ? { refresh_token: refresh } : {}),
            auth_domain: env.SF_AUTH_DOMAIN,
            client_id: env.SF_CLIENT_ID,
          });
        } else {
          nextToken = await refreshTokenApi(env, refresh!);
        }
        setAuthAndPersist({ ...(current ?? {}), tokenSet: nextToken });
      } catch {
        await doLogout();
        try {
          if (!window.location.pathname.startsWith("/login")) {
            window.location.assign("/login?error=session_expired");
          }
        } catch {}
      }
    };
    const p = run().finally(() => {
      refreshInFlight.current = null;
    });
    refreshInFlight.current = p;
    return p;
  }, [doLogout, env, setAuthAndPersist]);

  /** Apply token set to Salesforce context/storage only. */
  const applyTokenSet = useCallback(
    (tokenSet: TokenSet) => {
      if (tokenSet.access_token) {
        const current = storage.readSfAuthSession();
        const next = { ...(current ?? {}), tokenSet };
        setAuthAndPersist(next);
      }
    },
    [setAuthAndPersist],
  );

  const lastFetchedForTokenRef = useRef<string | null>(null);
  useEffect(() => {
    const token = stored?.tokenSet?.access_token;
    if (!token) {
      lastFetchedForTokenRef.current = null;
      setIsUserLoading(false);
      return;
    }
    if (lastFetchedForTokenRef.current === token) {
      setIsUserLoading(false);
      return;
    }
    lastFetchedForTokenRef.current = token;
    let cancelled = false;
    setIsUserLoading(true);
    fetchUserInfo(env, token)
      .then((u) => {
        if (!cancelled) {
          const current = storage.readSfAuthSession() || {};
          setAuthAndPersist({ ...(current as SfAuth), user: u });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsUserLoading(false);
      });
    return () => {
      cancelled = true;
      setIsUserLoading(false);
    };
  }, [stored?.tokenSet?.access_token, env, setAuthAndPersist]);

  useEffect(() => {
    if (!isAuthenticated && env.SF_SILENT_SSO) {
      const path = window.location.pathname;
      const disableOnce =
        window.sessionStorage.getItem("SF_DISABLE_SILENT_ONCE") === "1";
      if (disableOnce) {
        window.sessionStorage.removeItem("SF_DISABLE_SILENT_ONCE");
        return;
      }
      if (path !== "/callback" && path !== "/login") {
        doLogin(false).catch(() => {});
      }
    }
  }, [doLogin, env.SF_SILENT_SSO, isAuthenticated]);

  useEffect(() => {
    const expiryMs = getTokenExpiryMs(stored?.tokenSet);
    if (!expiryMs) return;
    const now = Date.now();
    const delay = Math.max(0, expiryMs - now - 60_000);
    const hasRefresh = Boolean(stored?.tokenSet?.refresh_token);
    const useBackend = Boolean(getEnv("REACT_APP_BE_URL"));
    const id = window.setTimeout(() => {
      if (hasRefresh || useBackend) {
        doRefresh().catch(() => {});
      } else {
        doLogin(false).catch(() => {});
      }
    }, delay);
    return () => window.clearTimeout(id);
  }, [stored?.tokenSet, doLogin, doRefresh]);

  /**
   * Optional absolute idle timeout (minutes) without silent re-login when refresh is available.
   * Default 0 = disabled. Login is only forced here when there is no refresh path (no refresh_token and no BFF cookies).
   */
  useEffect(() => {
    const minutes = parseInt(
      getEnv("REACT_APP_SESSION_TIMEOUT_MINUTES") || "0",
      10,
    );
    if (!isFinite(minutes) || minutes <= 0) return;
    const hasRefresh = Boolean(stored?.tokenSet?.refresh_token);
    const useBackend = Boolean(getEnv("REACT_APP_BE_URL"));
    if (hasRefresh || useBackend) return;
    const start = stored?.tokenSet?.issued_at || Date.now();
    const target = start + minutes * 60_000;
    const now = Date.now();
    const delay = Math.max(0, target - now);
    const id = window.setTimeout(() => {
      doLogin(false).catch(() => {});
    }, delay);
    return () => window.clearTimeout(id);
  }, [stored?.tokenSet?.issued_at, stored?.tokenSet?.refresh_token, doLogin]);

  const value = useMemo<SalesforceAuthContextValue>(
    () => ({
      isAuthenticated,
      accessToken: stored?.tokenSet?.access_token,
      instanceUrl: stored?.tokenSet?.instance_url,
      idToken: stored?.tokenSet?.id_token,
      user: (stored?.user ?? null) as User,
      isUserLoading,
      login: doLogin,
      logout: doLogout,
      refresh: doRefresh,
      applyTokenSet,
      logoutAppOnly,
    }),
    [
      stored?.tokenSet?.access_token,
      stored?.tokenSet?.id_token,
      stored?.tokenSet?.instance_url,
      stored?.user,
      applyTokenSet,
      doLogin,
      doLogout,
      doRefresh,
      logoutAppOnly,
      isAuthenticated,
      isUserLoading,
    ],
  );

  return (
    <SalesforceAuthContext.Provider value={value}>
      {children}
    </SalesforceAuthContext.Provider>
  );
}

export function useSalesforceAuth(): SalesforceAuthContextValue {
  const ctx = useContext(SalesforceAuthContext);
  if (!ctx)
    throw new Error(
      "useSalesforceAuth must be used within SalesforceAuthProvider",
    );
  return ctx;
}

export function useOptionalSalesforceAuth():
  | SalesforceAuthContextValue
  | undefined {
  return useContext(SalesforceAuthContext);
}
